import { useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft, HelpCircle, BookOpen, Mail } from 'lucide-react'
import Navbar from '../components/layout/Navbar'
import Footer from '../components/layout/Footer'
import { usePageMeta } from '../hooks/usePageMeta'

function SupportPage() {
  usePageMeta({
    title: 'Rithmo Support',
    description: 'Get help with Rithmo. Browse guides or contact our support team at support@rithmo.ai.',
  })
  const location = useLocation()
  const navigate = useNavigate()

  // Legacy bookmark: /support#contact → dedicated contact page (not on /support)
  useEffect(() => {
    if (location.hash === '#contact') {
      navigate('/contact', { replace: true })
    }
  }, [location.hash, navigate])

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      <main className="flex-1 pt-20 md:pt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-gradient-primary flex items-center justify-center shadow-glow-sm shrink-0">
              <HelpCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Support Center</h1>
              <p className="text-sm text-gray-500">Self-service help and virtual support resources</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <Link
              to="/faqs"
              className="card-interactive flex items-start gap-3 p-4"
            >
              <HelpCircle className="w-7 h-7 text-primary-600 shrink-0" />
              <div>
                <h2 className="text-base font-semibold text-gray-900 mb-0.5">FAQs</h2>
                <p className="text-gray-600 text-sm leading-snug">
                  Answers to common questions about Rithmo, setup, and usage.
                </p>
              </div>
            </Link>
            <Link
              to="/documentation"
              className="card-interactive flex items-start gap-3 p-4"
            >
              <BookOpen className="w-7 h-7 text-primary-600 shrink-0" />
              <div>
                <h2 className="text-base font-semibold text-gray-900 mb-0.5">Documentation</h2>
                <p className="text-gray-600 text-sm leading-snug">
                  Guides for Zoom integration, in-meeting controls, and troubleshooting.
                </p>
              </div>
            </Link>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 shadow-sm">
            <div className="text-gray-700 space-y-6">
              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-2">Virtual Support</h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Use the resources above to resolve most questions on your own. For sales, partnerships,
                  press, or other business inquiries, use our{' '}
                  <Link to="/contact" className="text-primary-600 font-medium hover:underline">
                    Contact Us
                  </Link>{' '}
                  form.
                </p>
              </section>

              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary-600" />
                  Email Support
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  For product or technical help, email{' '}
                  <a
                    href="mailto:support@rithmo.ai"
                    className="text-primary-600 font-medium hover:underline"
                  >
                    support@rithmo.ai
                  </a>
                  . Please include your name, company, account email, and a short description of the issue.
                </p>
              </section>

              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-2">Support Hours</h2>
                <p className="text-gray-600 text-sm">Rithmo support is available:</p>
                <p className="text-gray-600 text-sm mt-1">
                  Monday to Friday
                  <br />
                  9:00 AM to 5:00 PM Central Time
                </p>
              </section>

              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-2">First Response SLA</h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  We aim to provide a first response within 1 business day for standard support requests.
                </p>
                <p className="text-gray-600 text-sm mt-2 leading-relaxed">
                  For critical production-impacting issues reported during support hours, we prioritize review as
                  quickly as possible.
                </p>
              </section>

              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-2">Self-Help Resources</h2>
                <ul className="list-disc pl-5 space-y-1.5 text-gray-600 text-sm">
                  <li>
                    <Link to="/faqs" className="text-primary-600 hover:underline">
                      FAQs
                    </Link>
                    : answers to common questions about Rithmo
                  </li>
                  <li>
                    <Link to="/documentation" className="text-primary-600 hover:underline">
                      Documentation
                    </Link>
                  </li>
                  <li>
                    <Link to="/privacy" className="text-primary-600 hover:underline">
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link to="/security-ai-privacy-whitepaper" className="text-primary-600 hover:underline">
                      Security &amp; AI Privacy Whitepaper
                    </Link>
                  </li>
                  <li>
                    <Link to="/terms" className="text-primary-600 hover:underline">
                      Terms of Service
                    </Link>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-2">Zoom App Support</h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  If your issue relates to connecting Zoom, authorizing the Zoom integration, opening the app in a
                  Zoom meeting, or disconnecting Zoom, mention that in your email so we can route your request correctly.
                </p>
              </section>

              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-2">Data, Privacy, and Deletion Requests</h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  For privacy or data deletion requests, contact{' '}
                  <a
                    href="mailto:privacy@rithmo.ai"
                    className="text-primary-600 font-medium hover:underline"
                  >
                    privacy@rithmo.ai
                  </a>
                  .
                </p>
              </section>

              <section>
                <h2 className="text-lg font-bold text-gray-900 mb-2">Before You Email Support</h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Please check the documentation and FAQs for:
                </p>
                <ul className="list-disc pl-5 mt-1.5 space-y-1 text-gray-600 text-sm">
                  <li>how to add and authorize the app</li>
                  <li>how to use Rithmo in Zoom</li>
                  <li>how to remove or disconnect the app</li>
                  <li>common troubleshooting steps</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default SupportPage
